import React, { FC } from 'react';
import styled from '@emotion/styled';
import { ProcessedTicketData, TicketElement } from './useTicketBuilder';
import { DEFAULT_TICKET_FONT_SIZE, getDefaultTicketStyling } from './DefaultStyling';
import { useStorage } from '~Components/Storage';
import useI18n from '~Translations';
import { LocalItem } from '~Types';

const Wrapper = styled.div`
  background-color: white;
  color: black;
  font-family: sans-serif;
  width: 300px;
  box-sizing: border-box;
  letter-spacing: 0.2px;

  p {
    margin: 0;
  }

  div,
  span,
  p {
    box-sizing: border-box;
  }
`;

interface Props {
  ticketData: ProcessedTicketData;
  formatCurrency: (amount: number) => string;
  isAppOrder?: boolean;
}

const TicketImageDesign: FC<Props> = ({ ticketData, formatCurrency, isAppOrder }) => {
  const [storage] = useStorage();
  const { i18n } = useI18n();

  // Use styling from ticketData if available (from template), otherwise fall back to default
  const ticketStyling = ticketData.styling || getDefaultTicketStyling({
    storage,
    i18n,
    isTextPrinting: false,
  });

  const makeStyleObject = (type: string): React.CSSProperties => {
    const styling = ticketStyling[type] || {};

    return {
      fontSize: styling.fontSize || DEFAULT_TICKET_FONT_SIZE,
      fontWeight: styling.fontWeight || 'normal',
      textAlign: styling.textAlign || 'left',
      color: styling.styleType === 'filled' ? 'white' : 'black',
      backgroundColor: styling.styleType === 'filled' ? 'black' : 'transparent',
      padding: styling.styleType === 'filled' || styling.styleType === 'outline' ? '2px 4px' : '0',
      letterSpacing: '0.2px',
      border: styling.styleType === 'outline' ? '1px solid black' : 'none',
      display:
        styling.styleType === 'filled' || styling.styleType === 'outline'
          ? 'inline-block'
          : 'block',
      fontStyle: styling.fontStyle,
      marginTop: styling.marginTop || 0,
      marginBottom: styling.marginBottom || 0,
      width: styling.width || 'auto',
      textTransform: styling.textTransform || 'unset',
      wordBreak: 'break-word',
    };
  };

  const renderItemDetails = (
    item: {
      count: number;
    } & LocalItem,
    isFromOtherStation = false,
  ) => {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '2px',
          marginTop: '2px',
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          marginBottom: '8px',
        }}>
        {/* Item variations */}
        {item.variations.map((variation, varIndex) => (
          <p
            key={`variation-${varIndex}`}
            style={{
              ...makeStyleObject(isFromOtherStation ? 'ITEM_FROM_OTHER_STATION' : 'VARIATION'),
              display: 'flex',
              alignItems: 'space-between',
              gap: '8px',
              width: '100%',
            }}>
            {ticketStyling.VARIATION.prefix || ''}
            {variation.name}{' '}
            {!isAppOrder &&
            ticketStyling.VARIATION.showPrice &&
            variation.price &&
            !isFromOtherStation ? (
              <span
                style={{
                  marginLeft: 'auto',
                }}>
                {formatCurrency(variation.price)}
              </span>
            ) : (
              ''
            )}
          </p>
        ))}

        {/* Removed variations */}
        {item.removedVariations.map((variation, varIndex) => (
          <p
            key={`removed-${varIndex}`}
            style={{
              ...makeStyleObject(
                isFromOtherStation ? 'ITEM_FROM_OTHER_STATION' : 'REMOVED_VARIATION',
              ),
            }}>
            {ticketStyling.REMOVED_VARIATION.prefix} {variation.name}
          </p>
        ))}

        {/* Item comments */}
        {item.message && (
          <p
            key={`message-${item.message}`}
            style={{
              ...makeStyleObject(isFromOtherStation ? 'ITEM_FROM_OTHER_STATION' : 'ITEM_COMMENT'),
            }}>
            {item.message}
          </p>
        )}
      </div>
    );
  };

  const renderElement = (element: TicketElement, key: string) => {
    const style = makeStyleObject(element.type);

    if (element.type === 'SPACE') {
      return <div key={key} style={{ height: `${element.height || 16}px` }} />;
    }

    if (element.type === 'DIVIDER') {
      return (
        <div
          key={key}
          style={{
            marginTop: '8px',
            marginBottom: '8px',
            width: '100%',
            borderBottom: '1px solid black',
          }}
        />
      );
    }

    if (element.type === 'MAIN_CONTENT') {
      // Render main content (groups)
      return (
        <div key={key}>
          {ticketData.groupsData.map((group, groupIndex) => (
            <div
              key={`group-${groupIndex}`}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: ticketStyling.GROUP.spaceBetweenItems || 10,
                borderTop:
                  groupIndex === 0 && ticketStyling.GROUP.divider
                    ? '1px solid black'
                    : 'none',
                borderBottom: ticketStyling.GROUP.divider ? '1px solid black' : 'none',
                paddingTop:
                  groupIndex === 0 ? (ticketStyling.GROUP.spaceBetweenGroups || 0) / 2 : 0,
                paddingBottom: (ticketStyling.GROUP.spaceBetweenGroups || 0) / 2,
                marginBottom: (ticketStyling.GROUP.spaceBetweenGroups || 0) / 2,
              }}>
              {/* Group title */}
              {group.title && (
                <p key={`group-title-${groupIndex}`} style={{ width: '100%' }}>
                  <span style={makeStyleObject('GROUP_TITLE')}>{group.title}</span>
                </p>
              )}

              {/* Items in group */}
              {group.items.map((item, itemIndex) => (
                <div
                  key={`item-${itemIndex}`}
                  style={{
                    display: 'flex',
                    width: '100%',
                    gap: '4px',
                  }}>
                  <div
                    style={{
                      ...makeStyleObject('ITEM_TITLE'),
                      textTransform: 'lowercase',
                    }}>
                    <span>{item.count}x</span>
                  </div>
                  <div style={{ flex: 1 }}>
                    {/* Item title */}
                    <div
                      style={{
                        ...makeStyleObject('ITEM_TITLE'),
                        display: 'flex',
                        alignItems: 'space-between',
                        gap: '8px',
                      }}>
                      <span
                        style={{
                          flex: 1,
                        }}>
                        {item.title}
                      </span>
                      {/* Show price if enabled and item has price */}
                      {!isAppOrder && ticketStyling.ITEM_TITLE.showPrice && item.amount && (
                        <div
                          style={{
                            marginLeft: 'auto',
                          }}>
                          {formatCurrency(item.amount - (item.discount || 0))}
                        </div>
                      )}
                    </div>
                    {item.variations.length > 0 ||
                    item.removedVariations.length > 0 ||
                    item.message
                      ? renderItemDetails(item)
                      : null}
                  </div>
                </div>
              ))}

              {/* Items from other stations */}
              {group.itemsFromOtherStations.length > 0 && (
                <div style={{ marginTop: '10px' }}>
                  {group.itemsFromOtherStations.map((item, itemIndex) => (
                    <div key={`external-${itemIndex}`}>
                      <p
                        style={{
                          ...makeStyleObject('ITEM_FROM_OTHER_STATION'),
                        }}>
                        {item.count}x {item.title}
                      </p>

                      {ticketStyling.ITEM_FROM_OTHER_STATION.showDetails &&
                        (item.variations.length > 0 ||
                          item.removedVariations.length > 0 ||
                          item.message) &&
                        renderItemDetails(item, true)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      );
    }

    if (!element.data) return null;

    return (
      <p key={key} style={{ width: '100%' }}>
        <span style={style}>{element.data}</span>
      </p>
    );
  };

  return (
    <Wrapper>
      {/* Render all elements in order */}
      {ticketData.elements.map((element, index) => {
        return renderElement(element, `element-${index}`);
      })}
    </Wrapper>
  );
};

export default TicketImageDesign;
